import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Upload,
  message,
  Card,
  Row,
  Col,
  Typography,
  Divider,
  Tag,
  Spin,
} from "antd";
import { PlusOutlined, UploadOutlined, FileExcelOutlined, FormOutlined } from "@ant-design/icons";
import * as XLSX from 'xlsx';
import api from "../../../api/axios";

const { Option } = Select;
const { Title, Text } = Typography;

const AddToInventory = () => {
  // State for modals and data
  const [manualModalVisible, setManualModalVisible] = useState(false);
  const [excelModalVisible, setExcelModalVisible] = useState(false);
  const [uploadStatusModalVisible, setUploadStatusModalVisible] = useState(false);

  // Data states
  const [spareparts, setSpareparts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [excelRows, setExcelRows] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [uploadStatus, setUploadStatus] = useState({ uploaded: [], notUploaded: [] });
  const [loading, setLoading] = useState(false);

  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  // Fetch initial data
  const fetchData = async () => {
    try {
      const [spRes, whRes] = await Promise.all([
        api.get("/api/spareparts"),
        api.get("/api/warehouses"),
      ]);
      setSpareparts(spRes.data);
      setWarehouses(whRes.data);
    } catch (err) {
      console.error(err);
      messageApi.error("Failed to load initial data");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle Excel file selection
  const handleExcelSelect = async (file) => {
    const excelFile = file.originFileObj || file;
    try {
      const data = await excelFile.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(worksheet);
      setExcelRows(rows);
      setExcelModalVisible(true);
    } catch (err) {
      console.error(err);
      messageApi.error("Failed to read the Excel file.");
    }
    return false; // Prevent antd's default upload behavior
  };

  // Submit Excel data to inventory
  const submitExcelData = async () => {
    if (!selectedWarehouse) {
      messageApi.error("Please select a warehouse first.");
      return;
    }
    setLoading(true);

    const uploaded = [];
    const notUploaded = [];

    for (const row of excelRows) {
      const sparepart = spareparts.find(sp => sp.importerId === row.importerId);

      if (!sparepart) {
        notUploaded.push({ ...row, reason: `Sparepart with Importer ID '${row.importerId}' not found.` });
        continue;
      }

      const payload = {
        sparepartId: sparepart.id,
        warehouseId: selectedWarehouse,
        quantity: row.quantity || 0,
        sku: row.sku || "",
      };

      try {
        await api.post("/api/inventories", payload);
        uploaded.push(row);
      } catch (err) {
        console.error(err);
        notUploaded.push({ ...row, reason: `API Error: ${err.response?.data?.message || err.message}` });
      }
    }

    setUploadStatus({ uploaded, notUploaded });
    setLoading(false);
    setExcelModalVisible(false);
    setUploadStatusModalVisible(true);
    
    // Clear state for next upload
    setExcelRows([]);
    setSelectedWarehouse(null);
  };

  // Single inventory form submission
  const handleManualSubmit = async (values) => {
    setLoading(true);
    try {
      await api.post("/api/inventories", values);
      messageApi.success("Inventory added successfully!");
      setManualModalVisible(false);
      form.resetFields();
    } catch (err) {
      console.error(err);
      messageApi.error("Failed to add inventory.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {contextHolder}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', padding: '24px' }}>
        <Row gutter={[24, 24]} justify="center" style={{ width: '100%', maxWidth: '800px' }}>
          {/* Card for Manual Add */}
          <Col xs={24} sm={12}>
            <Card
              hoverable
              onClick={() => setManualModalVisible(true)}
              style={{ textAlign: 'center', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
            >
              <FormOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
              <Title level={4} style={{ marginTop: '16px' }}>Add Manually</Title>
              <Text type="secondary">Add a single item to the inventory.</Text>
            </Card>
          </Col>

          {/* Card for Excel Upload */}
          <Col xs={24} sm={12}>
            <Upload
              beforeUpload={handleExcelSelect}
              accept=".xlsx,.xls"
              showUploadList={false}
              style={{ display: 'block' }}
            >
              <Card
                hoverable
                style={{ textAlign: 'center', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
              >
                <FileExcelOutlined style={{ fontSize: '48px', color: '#52c41a' }} />
                <Title level={4} style={{ marginTop: '16px' }}>Upload via Excel</Title>
                <Text type="secondary">Add multiple items from a spreadsheet.</Text>
              </Card>
            </Upload>
          </Col>
        </Row>
      </div>

      {/* Modal for Manual Add */}
            {/* Modal for Manual Add */}
      <Modal
        title="Add Inventory Manually"
        open={manualModalVisible}
        onCancel={() => setManualModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical" onFinish={handleManualSubmit}>
          <Form.Item name="sparepartId" label="Sparepart" rules={[{ required: true }]}>
            <Select
              showSearch
              placeholder="Search by Part Name, Part Number, or Importer ID..."
              // The default filterOption is not sufficient, so we provide a custom one.
              filterOption={(input, option) => {
                // 'option.searchableString' is a custom prop we will add to each <Option>
                // This makes the search logic clean and efficient.
                return (option?.searchableString ?? '').toLowerCase().includes(input.toLowerCase());
              }}
            >
              {spareparts.map((sp) => (
                <Option 
                  key={sp.id} 
                  value={sp.id}
                  // Create a custom prop that contains all searchable text
                  searchableString={`${sp.partName} ${sp.partNumber} ${sp.importerId}`}
                >
                  {/* The visible text can remain clean */}
                  {sp.partName} ({sp.partNumber}) - <Text type="secondary">{sp.importerId}</Text>
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="warehouseId" label="Warehouse" rules={[{ required: true }]}>
            <Select placeholder="Select Warehouse">
              {warehouses.map((wh) => <Option key={wh.id} value={wh.id}>{wh.name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="quantity" label="Quantity" rules={[{ required: true }]}>
            <Input type="number" min={0} />
          </Form.Item>
          <Form.Item name="sku" label="Location">
            <Input />
          </Form.Item>
        </Form>
      </Modal>


      {/* Modal for Excel Preview */}
      <Modal
        title="Preview Excel Data"
        open={excelModalVisible}
        onCancel={() => setExcelModalVisible(false)}
        onOk={submitExcelData}
        okText="Submit to Inventory"
        width={800}
        confirmLoading={loading}
      >
        <Form.Item label="Select Warehouse to Add Inventory" required>
          <Select placeholder="Select a warehouse..." value={selectedWarehouse} onChange={setSelectedWarehouse} style={{ width: '100%' }}>
            {warehouses.map((wh) => <Option key={wh.id} value={wh.id}>{wh.name}</Option>)}
          </Select>
        </Form.Item>
        <Divider>Preview of Rows</Divider>
        <Table
          dataSource={excelRows}
          columns={[
            { title: "Importer ID", dataIndex: "importerId", key: "importerId" },
            { title: "Part Name", dataIndex: "partName", key: "partName" },
            { title: "Quantity", dataIndex: "quantity", key: "quantity" },
            { title: "Location", dataIndex: "sku", key: "sku" },
          ]}
          rowKey={(row, index) => `${row.importerId}-${index}`}
          pagination={{ pageSize: 5 }}
          size="small"
          scroll={{ y: 240 }}
        />
      </Modal>

      {/* Modal for Upload Status */}
      <Modal
        title="Excel Upload Status"
        open={uploadStatusModalVisible}
        onCancel={() => setUploadStatusModalVisible(false)}
        footer={[<Button key="ok" type="primary" onClick={() => setUploadStatusModalVisible(false)}>OK</Button>]}
        width={800}
      >
        <Title level={5}>Successfully Uploaded ({uploadStatus.uploaded.length})</Title>
        <Table
          dataSource={uploadStatus.uploaded}
          columns={[
            { title: "Importer ID", dataIndex: "importerId", key: "importerId" },
            { title: "Quantity", dataIndex: "quantity", key: "quantity" },
            { title: "Status", key: "status", render: () => <Tag color="success">Uploaded</Tag> },
          ]}
          rowKey={(row, index) => `s-${index}`}
          pagination={{ pageSize: 3 }}
          size="small"
        />
        <Divider />
        <Title level={5}>Not Uploaded ({uploadStatus.notUploaded.length})</Title>
        <Table
          dataSource={uploadStatus.notUploaded}
          columns={[
            { title: "Importer ID", dataIndex: "importerId", key: "importerId" },
            { title: "Quantity", dataIndex: "quantity", key: "quantity" },
            { title: "Reason", dataIndex: "reason", key: "reason", render: (text) => <Tag color="error">{text}</Tag> },
          ]}
          rowKey={(row, index) => `f-${index}`}
          pagination={{ pageSize: 3 }}
          size="small"
        />
      </Modal>
    </>
  );
};

export default AddToInventory;
